import {
  MATCH_COPY_SPORT_OPTIONS,
  MATCH_COPY_STYLE_OPTIONS,
  MATCH_COPY_VARIANT_OPTIONS,
  buildMatchCopyContext,
  buildMatchCopyLookupPaths,
  buildMatchCopyLookupRoots,
  getMatchCopyScopedPath,
  type MatchCopyContext,
  type MatchCopyScope,
  type MatchCopyStyle,
} from '../shared/matchCopyContext';
import {
  LOCATION_COMPOSER_COPY_DEFAULTS,
  type LocationComposerCopySettings,
} from '../shared/locationComposerCopy';

export type LocationCopyField = {
  path: string;
  label: string;
  section: 'intro' | 'search' | 'selection' | 'browse' | 'empty';
  help?: string;
};

export type LocationCopyPreviewContext = MatchCopyContext;
export type LocationCopyEditorScope = MatchCopyScope;
export type { MatchCopyStyle };

export const LOCATION_COPY_DEFAULTS: LocationComposerCopySettings = LOCATION_COMPOSER_COPY_DEFAULTS;

export const LOCATION_COPY_FIELDS: LocationCopyField[] = [
  { path: 'intro.title', label: 'Intro title', section: 'intro' },
  { path: 'intro.subtitle', label: 'Intro subtitle', section: 'intro' },
  { path: 'search.placeholder', label: 'Search placeholder', section: 'search' },
  { path: 'selectedLocation.placeholder', label: 'Selected location placeholder', section: 'selection' },
  { path: 'primaryCta.label', label: 'Primary CTA label', section: 'selection' },
  { path: 'tabs.topSpots', label: 'Top spots label', section: 'browse' },
  { path: 'tabs.recentActivity', label: 'Recent activity label', section: 'browse' },
  { path: 'emptyState.title', label: 'Empty state title', section: 'empty' },
  { path: 'emptyState.body', label: 'Empty state body', section: 'empty' },
];

export const LOCATION_COPY_STYLE_EDITOR_OPTIONS = MATCH_COPY_STYLE_OPTIONS;
export const LOCATION_COPY_SPORT_EDITOR_OPTIONS = MATCH_COPY_SPORT_OPTIONS;
export const LOCATION_COPY_VARIANT_EDITOR_OPTIONS = MATCH_COPY_VARIANT_OPTIONS;

export const LOCATION_COPY_PREVIEW_SAMPLE_VARS = {
  copyStyle: 'game',
  sportId: 'soccer',
  variant: null,
  activityLabel: 'Soccer',
  activityQuestionPhrase: 'playing soccer',
  activityPlanPhrase: 'a game',
  activityPrimaryCta: 'Play Soccer',
};

export function isObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function deepMerge(base: unknown, override: unknown): unknown {
  if (!isObject(base) || !isObject(override)) return override ?? base;
  const out: Record<string, unknown> = { ...base };
  Object.entries(override).forEach(([key, value]) => {
    out[key] = deepMerge(base[key], value);
  });
  return out;
}

export function deepDiff(base: unknown, value: unknown): unknown | undefined {
  if (value === undefined) return undefined;
  if (!isObject(base) || !isObject(value)) return value === base ? undefined : value;
  const out: Record<string, unknown> = {};
  const keys = new Set([...Object.keys(base), ...Object.keys(value)]);
  keys.forEach((key) => {
    const next = deepDiff(base[key], value[key]);
    if (next !== undefined) out[key] = next as unknown;
  });
  return Object.keys(out).length ? out : undefined;
}

export function cloneValue<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export function getPathValue(source: unknown, path: string): unknown {
  const parts = path.split('.').filter(Boolean);
  let current: unknown = source;
  for (const part of parts) {
    if (!isObject(current)) return undefined;
    current = current[part];
  }
  return current;
}

export function setPathValue(target: Record<string, unknown>, path: string, value: string) {
  const parts = path.split('.').filter(Boolean);
  if (!parts.length) return;
  let current: Record<string, unknown> = target;
  for (let index = 0; index < parts.length - 1; index += 1) {
    const part = parts[index];
    if (!isObject(current[part])) current[part] = {};
    current = current[part] as Record<string, unknown>;
  }
  current[parts[parts.length - 1]] = value;
}

export function deletePathValue(target: Record<string, unknown>, path: string) {
  const parts = path.split('.').filter(Boolean);
  if (!parts.length) return;
  let current: Record<string, unknown> = target;
  for (let index = 0; index < parts.length - 1; index += 1) {
    const part = parts[index];
    if (!isObject(current[part])) return;
    current = current[part] as Record<string, unknown>;
  }
  delete current[parts[parts.length - 1]];
}

export function normalizeLocationComposerOverrides(value: Record<string, unknown>) {
  const merged = deepMerge(LOCATION_COPY_DEFAULTS, value);
  return (deepDiff(LOCATION_COPY_DEFAULTS, merged) as Record<string, unknown> | undefined) ?? {};
}

export function getContextualPathValue(
  source: unknown,
  path: string,
  context?: Partial<LocationCopyPreviewContext> | null
): unknown {
  const candidates = buildMatchCopyLookupPaths(path, context);
  for (const candidate of candidates) {
    const value = getPathValue(source, candidate);
    if (value !== undefined) return value;
  }
  return undefined;
}

export function getEffectiveLocationComposerCopy(
  overrides: Record<string, unknown>,
  context?: Partial<LocationCopyPreviewContext> | null
) {
  const effective = deepMerge(LOCATION_COPY_DEFAULTS, overrides) as Record<string, unknown>;
  if (!context) return effective;
  const resolvedContext = buildMatchCopyContext(context);
  let merged = effective;
  [...buildMatchCopyLookupRoots(resolvedContext)].reverse().forEach((candidate) => {
    const scopedValue = getPathValue(effective, candidate);
    if (isObject(scopedValue)) merged = deepMerge(merged, scopedValue) as Record<string, unknown>;
  });
  return merged;
}

export function textValue(value: unknown) {
  return typeof value === 'string' ? value : '';
}

export function countOverridesForFields(
  overrides: Record<string, unknown>,
  fields: LocationCopyField[],
  scope: LocationCopyEditorScope = { kind: 'base' }
) {
  return fields.filter((field) => textValue(getPathValue(overrides, getMatchCopyScopedPath(field.path, scope))).trim().length > 0).length;
}

export function resolveScopedFieldPath(path: string, scope: LocationCopyEditorScope) {
  return getMatchCopyScopedPath(path, scope);
}

export function fieldsForSection(section: LocationCopyField['section']) {
  return LOCATION_COPY_FIELDS.filter((field) => field.section === section);
}

export function sectionLabel(section: LocationCopyField['section']) {
  switch (section) {
    case 'intro':
      return 'Intro';
    case 'search':
      return 'Search';
    case 'selection':
      return 'Selection';
    case 'browse':
      return 'Browse';
    case 'empty':
      return 'Empty state';
    default:
      return section;
  }
}
